import React, { useState } from 'react';

import BcLogo from 'Assets/landing-page/BC.svg';
import GoLogo from 'Assets/landing-page/GO.svg';
import { AlexProducts } from 'Containers/App/types';
import { forceEnglishForBcPath } from 'Containers/App/utils';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useTextContext } from 'Contexts/textContext';
import { Button, Icon, IconTypes, Paragraph } from 'DesignLibrary/atoms';
import { TextNote } from 'DesignLibrary/molecules/TextNote';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { INTRO_PATH, PROFILE_PATH, getEnvironmentFromUrl } from 'Utils/urls';

import { BulletPoint } from './BulletPoint';
import {
  ContinueContainer,
  ProductSelectContainer,
  SpanishDisclaimer,
  ToggleContainer,
  BottomSection,
} from './styled';
import { ToggleItem as ToggleItemOld } from './ToggleItem';
import { ToggleItem2025 } from './ToggleItem2025';
import { DEPLOY_ENVIRONMENTS } from '../../../../constants';

export interface ProductSelectToggleProps {
  handleNext: (path: string) => void;
  chooseProduct: (product: AlexProducts) => void;
  isIntegratedUser: boolean;
}

export const ProductSelectToggle = ({
  handleNext,
  chooseProduct,
  isIntegratedUser,
}: ProductSelectToggleProps) => {
  const [selectedProduct, setSelectedProduct] = useState<AlexProducts | null>(null);
  const { locale, retrieveContentfulData, setLocale } = useTextContext();
  const { is_ulp_updates_enabled } = useFeatureFlagContext();
  const ToggleItem = is_ulp_updates_enabled ? ToggleItem2025 : ToggleItemOld;

  // The BC (audio) experience is English-only; the matching switch to English on select
  // is handled by forceEnglishForBcPath.
  const showAudioDisclaimer = locale !== 'en-US';

  const handleContinue = () => {
    chooseProduct(selectedProduct as AlexProducts);
    if (selectedProduct === 'go') {
      handleNext(PROFILE_PATH);
    }
    if (selectedProduct === 'bc') {
      forceEnglishForBcPath(locale, setLocale);
      handleNext(INTRO_PATH);
    }
  };

  const renderBullets = (product: 'bc' | 'go') => {
    let bullets: string[];
    let icons: IconTypes[];

    if (product === 'bc') {
      bullets = retrieveContentfulData<string[]>(
        'unified_landing_page.choose_experience_modal.option1_bullets',
        [],
      );
      icons = ['ListChecks', 'SpeakerHigh', 'ClosedCaptioning'];
    } else {
      bullets = retrieveContentfulData<string[]>(
        'unified_landing_page.choose_experience_modal.option2_bullets',
        [],
      );
      icons = ['Lightning', 'FileText', 'Scales'];
    }

    return bullets.map((i: string, idx: number) => (
      <BulletPoint id={`bullets-${product}-${idx}`} icon={icons[idx]} text={i} key={idx} />
    ));
  };

  const getAriaLabelledByFromBullets = (bullets) => (bullets.length > 0 ? bullets[0].props.id : '');
  const getAriaDescribedByFromBullets = (bullets) =>
    bullets.length > 0
      ? bullets
          .slice(1)
          .map((e) => e.props.id)
          .join(' ')
      : '';

  const bc_bullets = renderBullets('bc');
  const bc_bullets_labelledBy = getAriaLabelledByFromBullets(bc_bullets);
  const bc_bullets_describedBy = getAriaDescribedByFromBullets(bc_bullets);

  const go_bullets = renderBullets('go');
  const go_bullets_labelledBy = getAriaLabelledByFromBullets(go_bullets);
  const go_bullets_describedBy = getAriaDescribedByFromBullets(go_bullets);

  return (
    <ProductSelectContainer>
      <ToggleContainer>
        <ToggleItem
          product="bc"
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          image={BcLogo}
          ariaLabelledBy={bc_bullets_labelledBy}
          ariaDescribedBy={bc_bullets_describedBy}
        >
          {bc_bullets}
          {showAudioDisclaimer && (
            <SpanishDisclaimer>
              <Icon type="Warning" color="--primary-red" />
              <Paragraph size="mini" weight="bold" color="--primary-red">
                {/* Contentful field is named spanish_support_disclaimer for historical reasons — it now holds multi-language support disclaimer text (Spanish, Chinese, etc.). Renaming would require creating a new field in Contentful as existing field IDs cannot be changed. */}
                <Text field="unified_landing_page.choose_experience_modal.spanish_support_disclaimer" />
              </Paragraph>
            </SpanishDisclaimer>
          )}
        </ToggleItem>

        <ToggleItem
          product="go"
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          image={GoLogo}
          ariaLabelledBy={go_bullets_labelledBy}
          ariaDescribedBy={go_bullets_describedBy}
        >
          {go_bullets}
        </ToggleItem>
      </ToggleContainer>
      {getEnvironmentFromUrl() !== DEPLOY_ENVIRONMENTS.PROD && !isIntegratedUser ? (
        <BottomSection>
          <TextNote
            mt={6}
            shortDisplayText={
              <RichText
                inlineParagraphs={false}
                field="unified_landing_page.choose_experience_modal.reviewer_note"
                useStylingWrapper
                richTextStyling={{ p: { size: 'small', color: 'var(--text-black)' } }}
              />
            }
          />

          <ContinueContainer>
            <div className="product-select-continue-button">
              <Button buttonType="primary" isDisabled={!selectedProduct} onClick={handleContinue} stretch>
                <Text field="unified_landing_page.choose_experience_modal.button" />
              </Button>
            </div>
          </ContinueContainer>
        </BottomSection>
      ) : (
        <ContinueContainer>
          <div className="product-select-continue-button">
            <Button buttonType="primary" isDisabled={!selectedProduct} onClick={handleContinue} stretch>
              <Text field="unified_landing_page.choose_experience_modal.button" />
            </Button>
          </div>
        </ContinueContainer>
      )}
    </ProductSelectContainer>
  );
};
